import React, { useMemo } from 'react';
import { Paper, Typography, Stack, Box, IconButton, Tooltip } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';

import { useGetOrderTimeline } from '../../../hooks/orders/useGetOrderTimeline';

// Status values that imply the order has been (or was) shipped at some point.
const SHIPPED_STATUSES = ['shipped', 'delivered'];

const OrderShippingCard = ({ order }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const isShipped = SHIPPED_STATUSES.includes(order?.status);

    // Only fetch the timeline when needed — ideally react-query dedupes this
    // with OrderTimeline if the same query key is in flight, otherwise it's
    // just one extra cached call.
    const { events } = useGetOrderTimeline(isShipped ? order?.id : null, { limit: 100 });

    const shippedInfo = useMemo(() => {
        if (!Array.isArray(events) || events.length === 0) return null;

        // Find the latest event whose payload carries tracking metadata.
        // Backend Sprint 11.1 records tracking_number under the EVENT_SHIPPED
        // status_changed payload (payload.to === 'shipped').
        const matches = events.filter((ev) => {
            if (ev.event === 'status_changed' && ev.payload?.to === 'shipped') return true;
            if (ev.event === 'order.shipped') return true;
            return Boolean(ev.payload?.tracking_number);
        });

        if (matches.length === 0) return null;

        // Latest by created_at
        const sorted = [...matches].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        const ev = sorted[0];
        return {
            tracking_number: ev.payload?.tracking_number || null,
            carrier: ev.payload?.carrier || null,
            shipped_at: ev.created_at,
        };
    }, [events]);

    if (!isShipped) return null;

    const handleCopy = () => {
        if (!shippedInfo?.tracking_number) return;
        navigator.clipboard
            ?.writeText(shippedInfo.tracking_number)
            .then(() => {
                toast.success(isArabic ? 'تم نسخ رقم التتبع' : 'Tracking number copied');
            })
            .catch(() => {
                toast.error(isArabic ? 'تعذر النسخ' : 'Failed to copy');
            });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 2,
            }}
        >
            <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                {isArabic ? 'الشحن' : 'Shipping'}
            </Typography>

            <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        {isArabic ? 'رقم التتبع:' : 'Tracking number:'}
                    </Typography>
                    {shippedInfo?.tracking_number ? (
                        <>
                            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                                {shippedInfo.tracking_number}
                            </Typography>
                            <Tooltip title={isArabic ? 'نسخ' : 'Copy'}>
                                <IconButton size="small" onClick={handleCopy}>
                                    <ContentCopy fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    )}
                </Stack>

                {shippedInfo?.carrier && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            {isArabic ? 'شركة الشحن:' : 'Carrier:'}{' '}
                        </Typography>
                        <Typography variant="caption" fontWeight={500}>
                            {shippedInfo.carrier}
                        </Typography>
                    </Box>
                )}

                {shippedInfo?.shipped_at && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            {isArabic ? 'تاريخ الشحن:' : 'Shipped at:'}{' '}
                        </Typography>
                        <Typography variant="caption">
                            {dayjs(shippedInfo.shipped_at).format('YYYY-MM-DD HH:mm')}
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Paper>
    );
};

export default OrderShippingCard;
